import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Space, 
  Breadcrumb,
  Tabs,
  Badge,
  Tag,
  Dropdown,
  Menu,
  message,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Radio,
  Upload,
  Divider,
  Switch,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  SettingOutlined,
  CalendarOutlined,
  GiftOutlined,
  SendOutlined,
  UserOutlined,
  BellOutlined,
  UploadOutlined,
  LikeOutlined,
  SearchOutlined,
  CopyOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// 周期活动数据接口
interface ActivityData {
  key: string;
  name: string;
  type: string;
  status: 'active' | 'inactive' | 'draft' | 'completed';
  startDate: string;
  endDate: string;
  audience: string;
  createdBy: string;
}

// 优惠券数据接口
interface CouponData {
  key: string;
  name: string;
  type: string;
  value: number;
  minSpend: number;
  validity: number;
  quantity: number;
  usedCount: number;
  status: 'active' | 'inactive' | 'expired';
}

// 群发消息数据接口
interface MessageData {
  key: string;
  title: string;
  content: string;
  audience: string;
  audienceCount: number;
  sendTime: string;
  status: 'scheduled' | 'sent' | 'draft';
  openRate?: number;
}

// 模拟数据
const activityData: ActivityData[] = [
  {
    key: '1',
    name: '周年庆优惠活动',
    type: '折扣活动',
    status: 'active',
    startDate: '2023-10-01',
    endDate: '2023-10-07',
    audience: '全部客户',
    createdBy: '张三',
  },
  {
    key: '2',
    name: '新客专享礼包',
    type: '新客礼',
    status: 'inactive',
    startDate: '2023-11-01',
    endDate: '2023-11-30',
    audience: '新注册用户',
    createdBy: '李四',
  },
  {
    key: '3',
    name: '国庆黄金周特惠',
    type: '限时促销',
    status: 'completed',
    startDate: '2023-10-01',
    endDate: '2023-10-07',
    audience: '全部客户',
    createdBy: '王五',
  },
  {
    key: '4',
    name: '会员生日专享',
    type: '生日礼',
    status: 'active',
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    audience: 'VIP会员',
    createdBy: '张三',
  },
  {
    key: '5',
    name: '双十一预热',
    type: '折扣活动',
    status: 'draft',
    startDate: '2023-11-01',
    endDate: '2023-11-10',
    audience: '全部客户',
    createdBy: '赵六',
  },
];

const couponData: CouponData[] = [
  {
    key: '1',
    name: '新客立减50元',
    type: '满减券',
    value: 50,
    minSpend: 300,
    validity: 30,
    quantity: 1000,
    usedCount: 356,
    status: 'active',
  },
  {
    key: '2',
    name: '餐饮8.5折',
    type: '折扣券',
    value: 85,
    minSpend: 200,
    validity: 15,
    quantity: 500,
    usedCount: 213,
    status: 'active',
  },
  {
    key: '3',
    name: '住宿满1000减200',
    type: '满减券',
    value: 200,
    minSpend: 1000,
    validity: 60,
    quantity: 300,
    usedCount: 89,
    status: 'inactive',
  },
  {
    key: '4',
    name: '生日专享礼品券',
    type: '礼品券',
    value: 0,
    minSpend: 0,
    validity: 15,
    quantity: 200,
    usedCount: 45,
    status: 'active',
  },
  {
    key: '5',
    name: '五一特惠券',
    type: '满减券',
    value: 100,
    minSpend: 500,
    validity: 7,
    quantity: 1000,
    usedCount: 980,
    status: 'expired',
  },
];

const messageData: MessageData[] = [
  {
    key: '1',
    title: '周年庆活动邀请',
    content: '尊敬的客户，我们的周年庆活动即将开始，期待您的参与...',
    audience: '全部客户',
    audienceCount: 1200,
    sendTime: '2023-10-01 09:00:00',
    status: 'sent',
    openRate: 68.5,
  },
  {
    key: '2',
    title: '会员专享优惠通知',
    content: '尊敬的会员，感谢您一直以来的支持，现有专属优惠等您来领取...',
    audience: 'VIP会员',
    audienceCount: 450,
    sendTime: '2023-10-15 10:00:00',
    status: 'scheduled',
  },
  {
    key: '3',
    title: '节日祝福',
    content: '祝您节日快乐，我们为您准备了节日专属礼遇...',
    audience: '活跃客户',
    audienceCount: 800,
    sendTime: '2023-09-29 08:00:00',
    status: 'sent',
    openRate: 72.3,
  },
  {
    key: '4',
    title: '新餐厅开业通知',
    content: '我们的新餐厅即将开业，诚邀您莅临品鉴...',
    audience: '本地客户',
    audienceCount: 650,
    sendTime: '',
    status: 'draft',
  },
];

const ActivityCenterManagement: React.FC = () => {
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [activityForm] = Form.useForm();
  const [couponForm] = Form.useForm();
  const [messageForm] = Form.useForm();

  // 活动表格列定义
  const activityColumns: ColumnsType<ActivityData> = [
    {
      title: '活动名称',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <a>{text}</a>,
    },
    {
      title: '活动类型',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <>
          {status === 'active' && <Badge status="success" text="进行中" />}
          {status === 'inactive' && <Badge status="default" text="未开始" />}
          {status === 'draft' && <Badge status="warning" text="草稿" />}
          {status === 'completed' && <Badge status="error" text="已结束" />}
        </>
      ),
    },
    {
      title: '开始日期',
      dataIndex: 'startDate',
      key: 'startDate',
    },
    {
      title: '结束日期',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: '目标受众',
      dataIndex: 'audience',
      key: 'audience',
    },
    {
      title: '创建人',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EyeOutlined />} style={{ padding: 0 }}>查看</Button>
          <Button type="link" icon={<EditOutlined />} style={{ padding: 0 }}>编辑</Button>
          <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }}>删除</Button>
        </Space>
      ),
    },
  ];

  // 优惠券表格列定义
  const couponColumns: ColumnsType<CouponData> = [
    {
      title: '券名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '券类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        let color = '';
        if (type === '满减券') color = 'green';
        else if (type === '折扣券') color = 'blue';
        else if (type === '礼品券') color = 'purple';
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: '优惠值',
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => 
        record.type === '满减券' ? `¥${value}` : 
        record.type === '折扣券' ? `${value/10}折` : 
        '礼品',
    },
    {
      title: '使用门槛',
      dataIndex: 'minSpend',
      key: 'minSpend',
      render: (value) => value > 0 ? `满¥${value}` : '无门槛',
    },
    {
      title: '有效期(天)',
      dataIndex: 'validity',
      key: 'validity',
    },
    {
      title: '总数量',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: '已使用',
      dataIndex: 'usedCount',
      key: 'usedCount',
      render: (used, record) => `${used} (${Math.round(used/record.quantity*100)}%)`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <>
          {status === 'active' && <Badge status="success" text="生效中" />}
          {status === 'inactive' && <Badge status="default" text="未生效" />}
          {status === 'expired' && <Badge status="error" text="已过期" />}
        </>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} style={{ padding: 0 }}>编辑</Button>
          <Button type="link" icon={<CopyOutlined />} style={{ padding: 0 }}>复制</Button>
          {record.status === 'active' ? (
            <Button type="link" danger style={{ padding: 0 }}>停用</Button>
          ) : (
            <Button type="link" style={{ padding: 0, color: 'green' }}>启用</Button>
          )}
        </Space>
      ),
    },
  ];

  // 群发消息表格列定义
  const messageColumns: ColumnsType<MessageData> = [
    {
      title: '消息标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '内容摘要',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      width: 250,
    },
    {
      title: '目标受众',
      dataIndex: 'audience',
      key: 'audience',
    },
    {
      title: '受众人数',
      dataIndex: 'audienceCount',
      key: 'audienceCount',
    },
    {
      title: '发送时间',
      dataIndex: 'sendTime',
      key: 'sendTime',
      render: (time) => time || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <>
          {status === 'sent' && <Badge status="success" text="已发送" />}
          {status === 'scheduled' && <Badge status="processing" text="已排程" />}
          {status === 'draft' && <Badge status="default" text="草稿" />}
        </>
      ),
    },
    {
      title: '打开率',
      dataIndex: 'openRate',
      key: 'openRate',
      render: (rate) => (rate ? `${rate}%` : '-'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EyeOutlined />} style={{ padding: 0 }}>查看</Button>
          {record.status !== 'sent' && (
            <>
              <Button type="link" icon={<EditOutlined />} style={{ padding: 0 }}>编辑</Button>
              {record.status === 'draft' && (
                <Button type="link" style={{ padding: 0, color: 'green' }}>发送</Button>
              )}
            </>
          )}
          <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }}>删除</Button>
        </Space>
      ),
    },
  ];

  const handleCreateActivity = () => {
    activityForm.resetFields();
    setActivityModalVisible(true);
  };

  const handleCreateCoupon = () => {
    couponForm.resetFields();
    setCouponModalVisible(true);
  };

  const handleCreateMessage = () => {
    messageForm.resetFields();
    setMessageModalVisible(true);
  };

  const handleSaveActivity = () => {
    activityForm.validateFields().then(values => {
      console.log('创建活动:', values);
      message.success('活动创建成功');
      setActivityModalVisible(false);
    }).catch(err => {
      console.log('表单验证失败:', err);
    });
  };

  const handleSaveCoupon = () => {
    couponForm.validateFields().then(values => {
      console.log('创建优惠券:', values);
      message.success('优惠券创建成功');
      setCouponModalVisible(false);
    }).catch(err => {
      console.log('表单验证失败:', err);
    });
  };

  const handleSaveMessage = () => {
    messageForm.validateFields().then(values => {
      console.log('创建消息:', values);
      message.success('消息创建成功');
      setMessageModalVisible(false);
    }).catch(err => {
      console.log('表单验证失败:', err);
    });
  };

  return (
    <div className="activity-center-management">
      <Breadcrumb 
        style={{ marginBottom: 16 }} 
        items={[
          { title: '首页' },
          { title: '活动中心' },
        ]}
      />
      
      <Tabs defaultActiveKey="1" style={{ background: '#fff', padding: '16px' }}>
        <TabPane 
          tab={<span><CalendarOutlined />周期活动</span>} 
          key="1"
        >
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleCreateActivity}
              >
                创建活动
              </Button>
            </div>
            <div>
              <Space>
                <Select 
                  style={{ width: 150 }} 
                  placeholder="活动状态" 
                  allowClear
                  options={[
                    { value: 'active', label: '进行中' },
                    { value: 'inactive', label: '未开始' },
                    { value: 'draft', label: '草稿' },
                    { value: 'completed', label: '已结束' },
                  ]}
                />
                <RangePicker placeholder={['开始日期', '结束日期']} />
                <Button icon={<SearchOutlined />}>搜索</Button>
              </Space>
            </div>
          </div>
          
          <Table 
            columns={activityColumns} 
            dataSource={activityData}
            rowKey="key"
            pagination={{ pageSize: 5 }}
          />
        </TabPane>
        
        <TabPane 
          tab={<span><GiftOutlined />优惠券管理</span>} 
          key="2"
        >
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleCreateCoupon}
              >
                创建优惠券
              </Button>
            </div>
            <div>
              <Space>
                <Select 
                  style={{ width: 120 }} 
                  placeholder="券类型" 
                  allowClear
                  options={[
                    { value: '满减券', label: '满减券' },
                    { value: '折扣券', label: '折扣券' },
                    { value: '礼品券', label: '礼品券' },
                  ]}
                />
                <Select 
                  style={{ width: 120 }} 
                  placeholder="状态" 
                  allowClear
                  options={[
                    { value: 'active', label: '生效中' },
                    { value: 'inactive', label: '未生效' },
                    { value: 'expired', label: '已过期' },
                  ]}
                />
                <Button icon={<SearchOutlined />}>搜索</Button>
              </Space>
            </div>
          </div>
          
          <Table 
            columns={couponColumns} 
            dataSource={couponData}
            rowKey="key"
            pagination={{ pageSize: 5 }}
          />
        </TabPane>
        
        <TabPane 
          tab={<span><SendOutlined />定向群发</span>} 
          key="3"
        >
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleCreateMessage}
              >
                创建群发消息
              </Button>
            </div>
            <div>
              <Space>
                <Select 
                  style={{ width: 120 }} 
                  placeholder="状态" 
                  allowClear
                  options={[
                    { value: 'sent', label: '已发送' },
                    { value: 'scheduled', label: '已排程' },
                    { value: 'draft', label: '草稿' },
                  ]}
                />
                <RangePicker placeholder={['开始日期', '结束日期']} />
                <Button icon={<SearchOutlined />}>搜索</Button>
              </Space>
            </div>
          </div>
          
          <Table 
            columns={messageColumns} 
            dataSource={messageData}
            rowKey="key"
            pagination={{ pageSize: 5 }}
          />
        </TabPane>
        
        <TabPane 
          tab={<span><BellOutlined />特殊节日/生日活动</span>} 
          key="4"
        >
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '100px 0', flexDirection: 'column' }}>
            <BellOutlined style={{ fontSize: 48, color: '#ddd', marginBottom: 16 }} />
            <p>敬请期待</p>
          </div>
        </TabPane>
      </Tabs>
      
      {/* 创建活动弹窗 */}
      <Modal 
        title="创建活动" 
        open={activityModalVisible}
        width={700}
        onCancel={() => setActivityModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setActivityModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" onClick={() => {
            activityForm.submit();
          }}>
            保存为草稿
          </Button>,
          <Button key="submit" type="primary" onClick={handleSaveActivity}>
            创建并发布
          </Button>,
        ]}
      >
        <Form
          form={activityForm}
          layout="vertical"
          onFinish={handleSaveActivity}
        >
          <Form.Item
            name="name"
            label="活动名称"
            rules={[{ required: true, message: '请输入活动名称' }]}
          >
            <Input placeholder="请输入活动名称" />
          </Form.Item>
          
          <Form.Item
            name="type"
            label="活动类型"
            rules={[{ required: true, message: '请选择活动类型' }]}
          >
            <Select
              placeholder="请选择活动类型"
              options={[
                { value: '折扣活动', label: '折扣活动' },
                { value: '限时促销', label: '限时促销' },
                { value: '新客礼', label: '新客礼' },
                { value: '会员活动', label: '会员活动' },
                { value: '生日礼', label: '生日礼' },
              ]}
            />
          </Form.Item>
          
          <Form.Item
            name="dateRange"
            label="活动时间范围"
            rules={[{ required: true, message: '请选择活动时间范围' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="audience"
            label="目标受众"
            rules={[{ required: true, message: '请选择目标受众' }]}
          >
            <Select
              placeholder="请选择目标受众"
              options={[
                { value: '全部客户', label: '全部客户' },
                { value: '新注册用户', label: '新注册用户' },
                { value: 'VIP会员', label: 'VIP会员' },
                { value: '活跃客户', label: '活跃客户' },
                { value: '流失客户', label: '流失客户' },
              ]}
            />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="活动描述"
          >
            <TextArea rows={4} placeholder="请输入活动描述" />
          </Form.Item>
          
          <Form.Item
            name="rewards"
            label="活动奖励"
          >
            <Select
              placeholder="请选择活动奖励"
              mode="multiple"
              options={[
                { value: '满减券', label: '满减券' },
                { value: '折扣券', label: '折扣券' },
                { value: '礼品券', label: '礼品券' },
                { value: '积分奖励', label: '积分奖励' },
              ]}
            />
          </Form.Item>
          
          <Form.Item
            name="notification"
            label="活动通知"
            valuePropName="checked"
          >
            <Switch checkedChildren="开启" unCheckedChildren="关闭" defaultChecked />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 创建优惠券弹窗 */}
      <Modal 
        title="创建优惠券" 
        open={couponModalVisible}
        width={600}
        onCancel={() => setCouponModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setCouponModalVisible(false)}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={handleSaveCoupon}>
            创建
          </Button>,
        ]}
      >
        <Form
          form={couponForm}
          layout="vertical"
          onFinish={handleSaveCoupon}
        >
          <Form.Item
            name="name"
            label="券名称"
            rules={[{ required: true, message: '请输入券名称' }]}
          >
            <Input placeholder="请输入券名称" />
          </Form.Item>
          
          <Form.Item
            name="type"
            label="券类型"
            rules={[{ required: true, message: '请选择券类型' }]}
          >
            <Select
              placeholder="请选择券类型"
              options={[
                { value: '满减券', label: '满减券' },
                { value: '折扣券', label: '折扣券' },
                { value: '礼品券', label: '礼品券' },
              ]}
            />
          </Form.Item>
          
          <Form.Item
            name="value"
            label="优惠值"
            rules={[{ required: true, message: '请输入优惠值' }]}
          >
            <Input addonBefore="¥" placeholder="请输入优惠值" />
          </Form.Item>
          
          <Form.Item
            name="minSpend"
            label="使用门槛"
            rules={[{ required: true, message: '请输入使用门槛' }]}
          >
            <Input addonBefore="满¥" placeholder="0表示无门槛" />
          </Form.Item>
          
          <Form.Item
            name="validity"
            label="有效期(天)"
            rules={[{ required: true, message: '请输入有效期' }]}
          >
            <Input addonAfter="天" placeholder="请输入有效期天数" />
          </Form.Item>
          
          <Form.Item
            name="quantity"
            label="发放数量"
            rules={[{ required: true, message: '请输入发放数量' }]}
          >
            <Input placeholder="请输入发放数量" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="使用说明"
          >
            <TextArea rows={3} placeholder="请输入使用说明" />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="状态"
            initialValue={true}
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="停用" defaultChecked />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 创建群发消息弹窗 */}
      <Modal 
        title="创建群发消息" 
        open={messageModalVisible}
        width={700}
        onCancel={() => setMessageModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setMessageModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" onClick={() => {
            messageForm.submit();
          }}>
            保存为草稿
          </Button>,
          <Button key="submit" type="primary" onClick={handleSaveMessage}>
            立即发送
          </Button>,
        ]}
      >
        <Form
          form={messageForm}
          layout="vertical"
          onFinish={handleSaveMessage}
        >
          <Form.Item
            name="title"
            label="消息标题"
            rules={[{ required: true, message: '请输入消息标题' }]}
          >
            <Input placeholder="请输入消息标题" />
          </Form.Item>
          
          <Form.Item
            name="content"
            label="消息内容"
            rules={[{ required: true, message: '请输入消息内容' }]}
          >
            <TextArea rows={6} placeholder="请输入消息内容" />
          </Form.Item>
          
          <Form.Item
            name="audience"
            label="目标受众"
            rules={[{ required: true, message: '请选择目标受众' }]}
          >
            <Select
              placeholder="请选择目标受众"
              options={[
                { value: '全部客户', label: '全部客户' },
                { value: '新注册用户', label: '新注册用户' },
                { value: 'VIP会员', label: 'VIP会员' },
                { value: '活跃客户', label: '活跃客户' },
                { value: '流失客户', label: '流失客户' },
                { value: '本地客户', label: '本地客户' },
              ]}
            />
          </Form.Item>
          
          <Form.Item
            name="sendType"
            label="发送方式"
            initialValue="now"
          >
            <Radio.Group>
              <Radio value="now">立即发送</Radio>
              <Radio value="schedule">定时发送</Radio>
            </Radio.Group>
          </Form.Item>
          
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.sendType !== currentValues.sendType}
          >
            {({ getFieldValue }) => 
              getFieldValue('sendType') === 'schedule' ? (
                <Form.Item
                  name="scheduledTime"
                  label="计划发送时间"
                  rules={[{ required: true, message: '请选择计划发送时间' }]}
                >
                  <DatePicker 
                    showTime 
                    format="YYYY-MM-DD HH:mm:ss" 
                    style={{ width: '100%' }} 
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          
          <Form.Item
            name="attachment"
            label="附件"
          >
            <Upload>
              <Button icon={<UploadOutlined />}>上传附件</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ActivityCenterManagement; 